import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Skeleton, Space, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useGetCountries } from 'api/countries/use-get-countries.ts';
import { useGetCargoTrips } from 'api/trips/use-get-cargo-trips';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import { useTripTaskStore } from 'shared/stores';
import { getCargoTypeLabel } from 'shared/utils/cargos/cargo-type-labels.ts';
import { findCountriesById } from 'shared/utils/countries';
import { formatWithPostfixOrEmpty } from 'shared/utils/format-with-postfix-or-empty';
import { CargoAddressCol } from 'components/cargos/cargo-address-col';
import { CargoInfoCol } from 'components/cargos/cargo-info-col';
import { CargoItemStatuses } from 'components/cargos/cargo-item-status';
import { CargoItemsTable } from 'components/cargos/cargos-list/components/cargo-items-table';
import { ColorCircles } from 'components/common/color-circles';
import { ImportantCargoDocumentFlags } from 'components/documents/important-cargo-document-flags';
import { TripCargoControl } from 'components/trips/trip-cargo-control';
import { TripCargoItemControl } from 'components/trips/trip-cargo-item-control';
import styles from './trip-cargo-list.module.scss';
import type { ITripCargoTableData, ITripCargoListProps } from './types';

export const TripCargoList: React.FC<ITripCargoListProps> = (props) => {
  const { trip } = props;
  const { data: countries } = useGetCountries();
  const { data: cargoTrips, isLoading } = useGetCargoTrips(trip.id);
  const transformedTableData = useTransformToTableData(cargoTrips || []);
  const [tableData, setTableData] = useState<ITripCargoTableData[]>(transformedTableData);

  const { cargoTrips: selectedCargoTrips, addCargoTrip, removeCargoTrip, clearCargoTrips } = useTripTaskStore();

  useEffect(() => {
    setTableData(transformedTableData);
  }, [transformedTableData]);

  useEffect(() => {
    return () => clearCargoTrips();
  }, [clearCargoTrips]);

  const selectedRowKeys = selectedCargoTrips.map((cargoTrip) => cargoTrip.id);
  const rowSelection: TableProps<ITripCargoTableData>['rowSelection'] = {
    selectedRowKeys,
    onSelect: (record, selected) => {
      if (selected) {
        addCargoTrip(record);
        return;
      }
      removeCargoTrip(record.id);
    },
    onSelectAll: (selected, _selectedRows, changeRows) => {
      if (selected) {
        changeRows.forEach((cargoTrip) => addCargoTrip(cargoTrip));
        return;
      }
      changeRows.forEach((cargoTrip) => removeCargoTrip(cargoTrip.id));
    },
  };

  const columns: ColumnsType<ITripCargoTableData> = [
    {
      title: 'ID',
      dataIndex: 'compositeId',
      width: '8%',
      render: (_, { cargoId, cargo }) => {
        const loadingCountry = findCountriesById(cargo.loadingAddress?.countryId, countries);
        const unloadingCountry = findCountriesById(cargo.unloadingAddress?.countryId, countries);
        return (
          <Space align='start' size='small' direction='vertical'>
            <Row justify='start' gutter={[8, 0]}>
              <Col>
                <Link to={`/cargos/${cargo.id}`}>{cargo.compositeId}</Link>
              </Col>
              <Col style={{ display: 'flex', alignItems: 'center' }}>
                <ColorCircles cargoId={cargoId} grayCircleClassnames={styles.grayDot} />
              </Col>
            </Row>
            <Row align='middle' justify='start'>
              <Col>
                <Tag>{getCargoTypeLabel(cargo.type)}</Tag>
              </Col>
              <Col>
                <Typography.Text className={styles.secondary}>
                  {loadingCountry?.code} <ArrowRightOutlined className={styles.arrow} /> {unloadingCountry?.code}
                </Typography.Text>
              </Col>
            </Row>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '16%',
      render: (_, record) => {
        return (
          <Space direction='vertical' size='small'>
            <Row gutter={2}>
              <Col>
                {record.cargoItemIds ? <Tag color='#f50'>Partial</Tag> : null}
                <CargoItemStatuses statuses={record.cargo.statuses} assetIsReturned={record.cargo.assetIsReturned} />
              </Col>
            </Row>
            <Row>
              <Col>
                <ImportantCargoDocumentFlags documents={record.cargo.documents} />
              </Col>
            </Row>
          </Space>
        );
      },
    },
    {
      title: 'Cost',
      dataIndex: 'costPrice',
      width: '7%',
      render: (costPrice: number | undefined, { costPriceByMap }) => {
        if (!costPrice && !costPriceByMap) {
          return null;
        }

        return (
          <>
            <div>{formatWithPostfixOrEmpty(costPrice, '€')}</div>
            <div className={styles.secondary}>{formatWithPostfixOrEmpty(costPriceByMap, '€')}</div>
          </>
        );
      },
    },
    {
      title: 'Info',
      dataIndex: 'totalWeight',
      width: '12%',
      render: (_, record) => {
        return <CargoInfoCol cargo={record.cargo} />;
      },
    },
    {
      title: 'Loading',
      dataIndex: 'loadingAddress',
      render: (_: unknown, record: ITripCargoTableData) => {
        return (
          <CargoAddressCol
            address={record.cargo.loadingAddress}
            customAddress={record.cargo.loadingCustomAddress}
            dateFrom={record.cargo.loadingDateFrom}
            dateTo={record.cargo.loadingDateTo}
          />
        );
      },
    },
    {
      title: 'Unloading',
      dataIndex: 'unloadingAddress',
      render: (_: unknown, record: ITripCargoTableData) => {
        return (
          <CargoAddressCol
            address={record.cargo.unloadingAddress}
            customAddress={record.cargo.unloadingCustomAddress}
            dateFrom={record.cargo.unloadingDateFrom}
            dateTo={record.cargo.unloadingDateTo}
          />
        );
      },
    },
    {
      title: '',
      dataIndex: 'action',
      render: (_, record) => <TripCargoControl cargoTrip={record} />,
    },
  ];

  const expandedRowRender = (record: ITripCargoTableData) => (
    <CargoItemsTable
      cargo={record.cargo}
      activeIds={record.cargoItemIds}
      actionsController={(cargoItem) => (
        <TripCargoItemControl
          trip={trip}
          cargoItem={cargoItem}
          cargoTripId={record.id}
          isInTrip={record.cargoItemIds === null || record.cargoItemIds?.includes(cargoItem.id) || false}
        />
      )}
      withoutCheckbox
    />
  );

  const tableComponent = (
    <Table
      rowSelection={rowSelection}
      expandable={{ expandedRowRender }}
      size='small'
      rowKey={(record) => record.id}
      loading={isLoading ? { size: 'large', delay: 200 } : false}
      columns={columns}
      dataSource={tableData}
      pagination={false}
      bordered
    />
  );

  return <Skeleton loading={isLoading}>{tableComponent}</Skeleton>;
};
